import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { MonitoringType } from "@/constants/monitoring";

/**
 * Cart Item Interface
 * Represents a single plan item added to the cart
 */
export interface CartItem {
  id: string; // Unique identifier for cart item (plan + monitoring type + platforms)
  planTitle: string;
  planPrice: string;
  monitoringType: MonitoringType;
  selectedPlatforms: string[];
  included: string[];
  hasFreeTrial: boolean;
  addedAt: number; // Timestamp for ordering
}

/**
 * Cart Store State Interface
 */
interface CartState {
  items: CartItem[];
  isOpen: boolean;
  addItem: (item: Omit<CartItem, "id" | "addedAt">) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  setIsOpen: (isOpen: boolean) => void;
  getTotalPrice: () => number;
  getItemCount: () => number;
}

/**
 * Generate unique cart item ID based on plan details
 */
function generateCartItemId(
  planTitle: string,
  monitoringType: MonitoringType,
  platforms: string[],
): string {
  const platformsStr = [...platforms].sort().join("-");
  return `${monitoringType}-${planTitle}-${platformsStr}`.toLowerCase();
}

/**
 * Parse price string to number
 * e.g., "$676/mo" => 676
 */
function parsePriceToNumber(priceStr: string): number {
  const match = priceStr.match(/\$?([\d,]+)/);
  if (!match) return 0;
  return parseInt(match[1].replace(/,/g, ""), 10);
}

/**
 * Zustand Store for Cart Management
 * Persisted to localStorage for cart persistence across sessions
 */
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      /**
       * Add item to cart
       * If item with same configuration exists, it won't be added again
       */
      addItem: (item) => {
        const id = generateCartItemId(
          item.planTitle,
          item.monitoringType,
          item.selectedPlatforms,
        );

        const existingItem = get().items.find((i) => i.id === id);

        // Prevent duplicate items
        if (existingItem) {
          console.warn("Item already exists in cart");
          return;
        }

        const newItem: CartItem = {
          ...item,
          id,
          addedAt: Date.now(),
        };

        set((state) => ({
          items: [...state.items, newItem],
        }));
      },

      /**
       * Remove item from cart by ID
       */
      removeItem: (itemId) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== itemId),
        }));
      },

      /**
       * Clear all items from cart
       */
      clearCart: () => {
        set({ items: [] });
      },

      /**
       * Toggle cart open/closed state
       */
      setIsOpen: (isOpen) => {
        set({ isOpen });
      },

      /**
       * Calculate total price of all items in cart
       */
      getTotalPrice: () => {
        return get().items.reduce((total, item) => {
          return total + parsePriceToNumber(item.planPrice);
        }, 0);
      },

      /**
       * Get total number of items in cart
       */
      getItemCount: () => {
        return get().items.length;
      },
    }),
    {
      name: "cart-storage",
      // Clean up old items on rehydration (optional: items older than 7 days)
      onRehydrateStorage: () => (state) => {
        if (state?.items) {
          const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          state.items = state.items.filter(
            (item) => item.addedAt > sevenDaysAgo,
          );
        }
      },
    },
  ),
);
